import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getDeleted, restoreDeleted, deleteForever, type DeletedMessage } from '@/api/inbox';
import { RotateCcw, Trash2, Clock } from 'lucide-react';
import { useUi } from '@/components/ui-dialogs';
import { useMailStore } from '@/stores/mail';
import { canErase } from '@/lib/access-tier';

function daysLeft(expiresAt: number): number {
  return Math.max(0, Math.ceil((expiresAt - Date.now() / 1000) / 86400));
}

function fmtDate(sec: number | null): string {
  if (!sec) return '';
  return new Date(sec * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function RecentlyDeletedRoute({
  mailboxId }: { mailboxId?: string } = {}) {
  const ui = useUi();
  const mailboxes = useMailStore((s) => s.mailboxes);
  const tierOf = (id: string) => mailboxes.find((mb) => mb.id === id)?.accessTier;
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['deleted', mailboxId ?? 'all'], queryFn: () => getDeleted(mailboxId) });
  const messages = data?.messages || [];
  const retentionDays = data?.retentionDays ?? 15;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['deleted'] });
    queryClient.invalidateQueries({ queryKey: ['unified-inbox'] });
    queryClient.invalidateQueries({ queryKey: ['messages'] });
  };
  const restore = useMutation({
    mutationFn: (m: DeletedMessage) => restoreDeleted(m.mailboxId, m.id),
    onSuccess: invalidate
  });
  const purge = useMutation({
    mutationFn: (m: DeletedMessage) => deleteForever(m.mailboxId, m.id),
    onSuccess: invalidate,
    onError: (e: Error) => ui.toast({ kind: 'error', message: e.message || 'Could not delete this message.' })
  });

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="border-b border-bmail-border px-4 py-4 md:px-6">
        <h1 className="flex items-center gap-2 text-xl font-bold text-bmail-text">
          <Trash2 size={20} /> Recently Deleted
        </h1>
        <p className="mt-1 text-sm text-bmail-text-secondary">
          Deleted mail is kept here for {retentionDays} days, then permanently removed.
        </p>
      </div>

      {isLoading ? (
        null
      ) : messages.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center p-10 text-center text-bmail-text-secondary">
          <Trash2 size={40} className="mb-3 opacity-40" />
          <p className="text-sm">Nothing here. Deleted messages will show up for {retentionDays} days.</p>
        </div>
      ) : (
        <ul className="divide-y divide-bmail-border">
          {messages.map((m) => {
            const left = daysLeft(m.expiresAt);
            return (
              <li key={m.id} className="px-4 py-3 md:px-6">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium text-bmail-text">{m.fromName || m.fromEmail || 'Unknown sender'}</span>
                      <span className="shrink-0 rounded bg-bmail-surface-hover px-1.5 py-0.5 text-[10px] text-bmail-text-secondary">{m.mailboxEmail}</span>
                    </div>
                    <div className="truncate text-sm text-bmail-text">{m.subject || '(no subject)'}</div>
                    {m.snippet && <div className="truncate text-xs text-bmail-text-secondary">{m.snippet}</div>}
                    <div className="mt-1 flex items-center gap-3 text-[11px] text-bmail-text-secondary">
                      <span>{fmtDate(m.date)}</span>
                      <span className="flex items-center gap-1">
                        <Clock size={11} /> {left === 0 ? 'clears today' : `clears in ${left} day${left === 1 ? '' : 's'}`}
                      </span>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    <button
                      onClick={() => restore.mutate(m)}
                      disabled={restore.isPending}
                      className="flex items-center gap-1 rounded-lg bg-bmail-goldfill px-2.5 py-1.5 text-xs font-medium text-bmail-on-accent hover:bg-bmail-goldfill-hover disabled:opacity-50"
                    >
                      <RotateCcw size={13} /> Restore
                    </button>
                    {canErase(tierOf(m.mailboxId)) && <button
                      onClick={async () => {
                        if (await ui.confirm({
                          title: 'Permanently delete this message?',
                          body: 'This one cannot be restored afterwards.',
                          confirmLabel: 'Delete forever', danger: true,
                        })) purge.mutate(m);
                      }}
                      disabled={purge.isPending}
                      className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-bmail-error hover:bg-bmail-error/10 disabled:opacity-50"
                    >
                      <Trash2 size={13} /> Delete forever
                    </button>}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
